import MUIDataTable from "mui-datatables";
import React, { useEffect, useState } from "react";
import { Button, message, Skeleton } from 'antd'
import { makeRequestStateless, handleDownloadFile } from "@utils/helpers";
import { generateReportFileReq, generateReportReq, getReportInitialDataReq } from "../requests";
import { useSelector } from "react-redux";
import { useLocation, useParams } from 'react-router-dom';
import { BodyComponent, HeaderComponent, CreateButton } from '@comps/components'
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import * as XLSX from "xlsx";
import { ExceptionOutlined, FilePdfOutlined } from "@ant-design/icons";

const GenerateReport = (props) => {

  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate()
  const authUser = useSelector((state) => state.auth.authUser);

  const [columns, setColumns] = useState([])
  const [dataSource, setDataSource] = useState([])
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState(null)
  const [meta, setMeta] = useState(null) // columns + conditions

  // ✅ Filters State
  const [filters, setFilters] = useState([])
  const [hiddenColumns, setHiddenColumns] = useState([])
  const [visibleColumns, setVisibleColumns] = useState([])
  const [filterList, setFilterList] = useState([])
  const [selectedColumns, setSelectedColumns] = useState([]);

  var columnsToDownload = []

  useEffect(() => {
    loadReportColumnsAndConditions(id);
    getInitialData([])
  }, []);

  const loadReportColumnsAndConditions = (id) => {
    makeRequestStateless(getReportInitialDataReq, { id }, (res) => {
      if (res.report.columns.length === 0) {
        res.report.columns = res.columns;
      }

      setMeta(res)
      setReport(res.report)
      // prepare columns for table
      const cols = res.report.columns.map(col => ({
        name: col.column_key,
        label: col.report_display_name,
        options: { filter: false, sort: true }
      }))
      setColumns(cols)

      // // Auto-add default date filters
      // const defaultDateFilters = res.report.columns
      //   .filter(col => col.column_type === "date" || col.column_type === "datetime")
      //   .map(col => ({
      //     column: col.column_key,
      //     type: col.column_type,
      //     operator: "=",
      //     value: null,
      //     is_related: col.is_related,
      //     is_custom: col.is_custom
      //   }));
      // setFilters(defaultDateFilters);

      // // Automatically fetch data with default filters
      // if (defaultDateFilters.length) 
      // 
      // applyFilters(defaultDateFilters);
    });
  }

  // =========================
  // ✅ FILTER LOGIC
  // =========================

  const addFilterRow = () => {
    setFilters(prev => [...prev, {
      column: "",
      operator: "",
      value: "",
      type: "",
      is_related: false,
      is_custom: false
    }]);
  }

  const removeFilter = (index) => {
    const newFilters = filters.filter((_, i) => i !== index);
    setFilters(newFilters);
    applyFilters(newFilters); // auto-update after remove
  }

  const updateFilter = (index, key, value) => {

    const updated = [...filters];
    updated[index][key] = value;

    if (key === "column") {
      const col = meta.report.columns && meta.report.columns.find(c => c.column_key === value);
      updated[index].type = col?.column_type;
      updated[index].is_related = col?.is_related;
      updated[index].is_custom = col?.is_custom;
      updated[index].operator = "";
      updated[index].value = col?.column_type === "date" || col?.column_type === "datetime" ? dayjs().format("YYYY-MM-DD") : "";
    }

    setFilters(updated);
  }

  const renderValueInput = (filter, index) => {
    if (!filter.operator) return null;

    const condition = meta.conditions[filter.type]?.find(
      c => c.operator === filter.operator
    );
    if (!condition?.require_value) return null;

    // Range inputs (From / To)
    if (condition.number_of_values === 2) {
      const inputType = filter.type === "integer" || filter.type === "double"
        ? "number"
        : filter.type === "date" || filter.type === "datetime"
          ? "date"
          : "text";

      return (
        <>
          <input
            type={ inputType }
            placeholder="From"
            value={ filter.value?.[0] || "" }
            onChange={ (e) =>
              updateFilter(index, "value", [e.target.value, filter.value?.[1]])
            }
          />
          <input
            type={ inputType }
            placeholder="To"
            value={ filter.value?.[1] || "" }
            onChange={ (e) =>
              updateFilter(index, "value", [filter.value?.[0], e.target.value])
            }
          />
        </>
      );
    }

    // Boolean input
    if (filter.type === "boolean") {
      return (
        <select value={ filter.value || "" } onChange={ (e) => updateFilter(index, "value", e.target.value) }>
          <option value="">Select</option>
          <option value="1">True</option>
          <option value="0">False</option>
        </select>
      );
    }

    // Single-value input
    const inputType = filter.type === "integer" || filter.type === "double"
      ? "number"
      : filter.type === "date" || filter.type === "datetime"
        ? "date"
        : "text";

    return (
      <input
        type={ inputType }
        placeholder="Value"
        value={ filter.value || "" }
        onChange={ (e) => updateFilter(index, "value", e.target.value) }
      />
    );
  };

  // =========================
  // ✅ APPLY FILTER (IMPORTANT)
  // =========================

  const applyFilters = (filtersToApply = filters) => {
    setFilters(filtersToApply);
    setLoading(true);
    getInitialData(filtersToApply);
  }

  const resetFilters = () => {
    setFilters([]);
    setDataSource([]);
  }

  const getInitialData = (filtersToApply) => {
    const payload = {
      id: id,
      preview_call: true,
      per_page: 50,
      filters: {
        filters: filtersToApply.map(f => ({
          field: f.column,
          operator: f.operator,
          value: f.operator === "LIKE" ? `%${f.value}%` : f.value,
          data_type: f.type,
          is_related: f.is_related,
          is_custom: f.is_custom
        }))
      }
    }
    makeRequestStateless(generateReportReq, payload, onSuccessReportDataReq, onErrorReportData);
  }

  const onSuccessReportDataReq = (response) => {
    setColumns(response.columns);
    const rawRows = response.report_data.data === undefined ? response.report_data : response.report_data.data;
    setDataSource(filterEmptyTableRows(rawRows))
    setReport(response.report)

    const newColumns = response.columns.map(column => ({
      name: column.column_key,
      label: column.report_display_name,
      options: { filter: true, sort: true }
    }));

    columnsToDownload = newColumns
    setVisibleColumns(newColumns)
    setColumns(newColumns)
    setLoading(false)
  }

  const onErrorReportData = (response) => { setLoading(false) }

  // =========================
  // TABLE OPTIONS
  // =========================

  const options = {
    draggable: true,
    draggableColumns: { enabled: true },
    responsive: 'standard',
    searchAlwaysOpen: true,
    filterType: 'multiselect',
    filter: true,
    download: true,
    onColumnViewChange: (element, action) => {
      if (action === 'add' && !selectedColumns.includes(element)) setSelectedColumns(prev => [...prev, element])
      if (action === 'remove' && selectedColumns.includes(element)) setSelectedColumns(prev => prev.filter(item => item !== element))
    },
    downloadOptions: { filename: `${report && report.title}-${dayjs().format('YYYY-MM-DD')}.xlsx`, separator: ',' },
    onDownload: (buildHead, buildBody, columns, allData) => {
      var filteredData = allData.filter((row) => {
        var ret = true;
        for (var i = 0; i <= columns.length; i++) {
          if (filterList[i] && filterList[i].length) {
            ret = ret && filterList[i].filter(fil => row.data[i] === fil).length > 0;
          }
        }
        return ret;
      });
      const dataForExport = [columns.map(col => col.label), ...filteredData.map(row => row.data.map(cell => cell))];
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(dataForExport);
      XLSX.utils.book_append_sheet(wb, ws, `Sheet1`);
      XLSX.writeFile(wb, `${report && report.title}.xlsx`);
      return false;
    },
  };


  const generateFile = (filtersToApply, format) => {

    const payload = {
      id: id,
      format: format,
      employment_id: authUser?.employment_id,
      filters: JSON.stringify({
        filters: filters.map(f => ({
          field: f.column,
          operator: f.operator,
          value: f.operator === "LIKE" ? `%${f.value}%` : f.value,
          data_type: f.type,
          is_related: f.is_related,
          is_custom: f.is_custom
        }))
      })
    }
    makeRequestStateless(generateReportFileReq, payload, onDownloadSuccess, onDownloadError);
    setLoading(true)
  }

  const onDownloadError = (err, res) => {
    setLoading(false)
    //message.error(res.message)
  }

  const onDownloadSuccess = (res) => {

    if (res) {
      setLoading(false)
      // Extract file extension
      const fileExtension = res.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? 'xlsx' : 'pdf'
      const fileName = `${report.title}_${dayjs().format('YYYY-MM-DD')}.${fileExtension}`
      //if (fileExtension === 'pdf') {
      handleDownloadFile(fileName, res)
      //}

    }
    else {
      message.error(res.message)
    }

  }

  return (
    <BodyComponent>
      <HeaderComponent modkey={ props.modkey }>
        <CreateButton
          onClick={ () => navigate(location.state?.returnTo || '/reports/all-reports', {
            state: {
              selectedReport: location.state?.selectedReport || (report ? {
                id: report.id,
                title: report.title,
                report_type_id: report.report_type_id || report.report_type?.id || null
              } : null)
            }
          }) }
          text='Back'
        />
      </HeaderComponent>

      {/* ========================= */ }
      {/* ✅ FILTER UI */ }
      {/* ========================= */ }
      { report && (
        <div style={ {
          padding: 25,
          borderBottom: "1px solid #ddd",
          backgroundColor: "#f9f9f9",
          borderRadius: 8,
          marginBottom: 20
        } }>
          { filters.map((filter, index) => {
            const conditions = meta?.conditions[filter.type] || [];
            return (
              <div key={ index } style={ {
                display: "flex",
                gap: 12,
                marginBottom: 12,
                padding: 10,
                backgroundColor: "#ffffff",
                border: "1px solid #e0e0e0",
                borderRadius: 6,
                alignItems: "center",
                transition: "box-shadow 0.2s",
              } }
                onMouseEnter={ e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)" }
                onMouseLeave={ e => e.currentTarget.style.boxShadow = "none" }
              >
                {/* Column */ }
                <select
                  value={ filter.column }
                  onChange={ (e) => updateFilter(index, "column", e.target.value) }
                  style={ { padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc" } }
                >
                  <option value="">Column</option>
                  { meta?.report?.columns.map(col => (
                    <option key={ col.column_key } value={ col.column_key }>
                      { col.report_display_name }
                    </option>
                  )) }
                </select>

                {/* Condition */ }
                <select
                  value={ filter.operator }
                  onChange={ (e) => updateFilter(index, "operator", e.target.value) }
                  style={ { padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc" } }
                >
                  <option value="">Condition</option>
                  { conditions.map(cond => (
                    <option key={ cond.id } value={ cond.operator }>{ cond.name }</option>
                  )) }
                </select>

                {/* Value */ }
                { renderValueInput(filter, index) }

                {/* Remove */ }
                <button
                  onClick={ () => removeFilter(index) }
                  style={ {
                    padding: "6px 10px",
                    backgroundColor: "#ff4d4f",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer"
                  } }
                >
                  X
                </button>
              </div>
            );
          }) }

          <div style={ { marginTop: 15, display: "flex", gap: 12 } }>
            <button
              onClick={ addFilterRow }
              style={ {
                padding: "8px 15px",
                backgroundColor: "#1890ff",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                cursor: "pointer"
              } }
            >
              + Add Filter
            </button>

            <button
              onClick={ () => applyFilters() }
              style={ {
                padding: "8px 15px",
                backgroundColor: "#52c41a",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                cursor: "pointer"
              } }
            >
              Load Data
            </button>

            <button
              onClick={ resetFilters }
              style={ {
                padding: "8px 15px",
                backgroundColor: "#faad14",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                cursor: "pointer"
              } }
            >
              Reset
            </button>
          </div>
        </div>
      ) }
      {/* ========================= */ }
      {/* TABLE */ }
      {/* ========================= */ }
      <Skeleton loading={ loading }>
        <Button className="text-success" loading={ loading } icon={ <ExceptionOutlined /> } onClick={ () => generateFile(filters, 'xlsx') }>EXCEL</Button>
        <Button className="text-danger" loading={ loading } icon={ <FilePdfOutlined /> } onClick={ () => generateFile(filters, 'pdf') }>PDF</Button>
        <MUIDataTable
          title={ report?.title }
          data={ dataSource }
          columns={ columns }
          options={ options }
        />
      </Skeleton>
    </BodyComponent>
  );
}

function filterEmptyTableRows(rows) {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.filter((row) => {
    if (Array.isArray(row)) {
      return row.length > 0 && row.some((cell) => cell !== null && cell !== undefined && cell !== "");
    }

    if (row && typeof row === "object") {
      const values = Object.values(row);
      return values.length > 0 && values.some((cell) => cell !== null && cell !== undefined && cell !== "");
    }

    return row !== null && row !== undefined && row !== "";
  });
}

export default GenerateReport;
